import { useState, useCallback, useEffect } from 'react';
import type { User } from '@/types';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface UserSaveData {
  name: string;
  email: string;
  cpf: string;
  password?: string;
  setor: string;
  perfil: 'admin' | 'colaborador';
  status: 'ativo' | 'inativo';
  forcePasswordReset?: boolean;
}

export function useUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterSetor, setFilterSetor] = useState('all');
  const [filterPerfil, setFilterPerfil] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchUsers = useCallback(async () => {
    if (!currentUser || currentUser.perfil !== 'admin') {
      setUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: supaError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (supaError) throw supaError;

      const mapped: User[] = (data || []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        name: row.name as string,
        cpf: ((row.cpf as string) || (row.matricula as string) || ''),
        email: row.email as string,
        setor: row.setor as string,
        perfil: row.perfil as 'admin' | 'colaborador',
        status: row.status as 'ativo' | 'inativo',
        mustChangePassword: (row.must_change_password as boolean) || false,
        createdAt: row.created_at as string,
      }));

      setUsers(mapped);
    } catch (err) {
      setError(err instanceof Error ?err.message : 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const addUser = useCallback(async (data: UserSaveData & { password: string }) => {
    setError(null);
    try {
      const { data: result, error: fnError } = await supabase.functions.invoke('manage-users', {
        body: {
          action: 'create',
          email: data.email,
          password: data.password,
          name: data.name,
          cpf: data.cpf,
          setor: data.setor,
          perfil: data.perfil,
          status: data.status,
          forcePasswordReset: true,
        },
      });

      if (fnError) throw fnError;
      if (result.error) throw new Error(result.error);

      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ?err.message : 'Erro ao criar usuário');
      throw err;
    }
  }, [fetchUsers]);

  const editUser = useCallback(async (id: string, data: Partial<UserSaveData>) => {
    setError(null);
    try {
      const { data: result, error: fnError } = await supabase.functions.invoke('manage-users', {
        body: {
          action: 'update',
          id,
          name: data.name,
          password: data.password,
          cpf: data.cpf,
          setor: data.setor,
          perfil: data.perfil,
          status: data.status,
          forcePasswordReset: data.forcePasswordReset,
        },
      });

      if (fnError) throw fnError;
      if (result.error) throw new Error(result.error);

      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ?err.message : 'Erro ao editar usuário');
      throw err;
    }
  }, [fetchUsers]);

  const importUsers = useCallback(async (rows: Array<UserSaveData & { password: string }>) => {
    setError(null);
    const results = {
      success: 0,
      errors: [] as string[],
    };

    for (const [index, row] of rows.entries()) {
      try {
        await addUser(row);
        results.success += 1;
      } catch (err) {
        results.errors.push(`Linha ${index + 2}: ${err instanceof Error ?err.message : 'Erro ao importar usuário'}`);
      }
    }

    await fetchUsers();
    return results;
  }, [addUser, fetchUsers]);

  const deleteUser = useCallback(async (id: string) => {
    setError(null);
    try {
      const { data: result, error: fnError } = await supabase.functions.invoke('manage-users', {
        body: { action: 'delete', id },
      });

      if (fnError) throw fnError;
      if (result.error) throw new Error(result.error);

      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ?err.message : 'Erro ao excluir usuário');
      throw err;
    }
  }, [fetchUsers]);

  const toggleStatus = useCallback(async (id: string) => {
    const target = users.find((u) => u.id === id);
    if (!target) return;

    const newStatus = target.status === 'ativo' ?'inativo' : 'ativo';
    try {
      await editUser(id, { status: newStatus as 'ativo' | 'inativo' });
    } catch {
      // error already set by editUser
    }
  }, [users, editUser]);

  const filteredUsers = users.filter((u) => {
    const matchSearch =
      !search ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.cpf.toLowerCase().includes(search.toLowerCase());
    const matchSetor = filterSetor === 'all' || u.setor === filterSetor;
    const matchPerfil = filterPerfil === 'all' || u.perfil === filterPerfil;
    const matchStatus = filterStatus === 'all' || u.status === filterStatus;
    return matchSearch && matchSetor && matchPerfil && matchStatus;
  });

  return {
    users: filteredUsers,
    totalUsers: users.length,
    loading,
    error,
    search,
    setSearch,
    filterSetor,
    setFilterSetor,
    filterPerfil,
    setFilterPerfil,
    filterStatus,
    setFilterStatus,
    addUser,
    editUser,
    deleteUser,
    toggleStatus,
    importUsers,
    refresh: fetchUsers,
  };
}
